import { DEMO_PROJECT } from './demo-project';
import type { GgxTourDefinition } from './tour-types';

export const RISK_08: GgxTourDefinition = {
    key: '[RISK-08] Using Models as Evaluators',
    route: '/home',
    project: DEMO_PROJECT,
    estimatedMinutes: 7,
    prerequisite: {
        target: '[test-id="home-page-card-Data & AI Assets"]',
        message: 'Open the home page, then start this tour.',
    },
    intro: {
        headline: 'When the grader is a model too',
        subtitle:
            'Some qualities have no simple right answer to compare against. Was the reply ' +
            'relevant? Was it polite? Did it actually address the question? For those, GGX ' +
            'can hand the grading to another language model, an LLM-as-a-Judge, that reads ' +
            'the answer against a written rubric and returns a score. This tour explains how ' +
            'that works, what to watch out for, and then opens a real judge in the Model ' +
            'Catalog so you can see its prompt, its building blocks and the report that ' +
            'checks how well it agrees with humans.',
    },
    outro: {
        title: "That's a wrap!",
        subtitle:
            'You have seen what an LLM-as-a-Judge is, where it shines and where it needs a ' +
            'human alongside it, and a few techniques that make a judge more reliable. You ' +
            'then found one on the Models page, saw that it is built from a prompt, a helper ' +
            'function and a foundation model like any other object, and opened the report that ' +
            'compares its scores with human labels. That comparison is the habit to keep: a ' +
            'judge is only as trustworthy as its agreement with people, so measure it before ' +
            'you let it grade at scale. Next, run an evaluation of your own.',
        learned: [
            'An **LLM-as-a-Judge** scores a model against a rubric.',
            'It is built from a **prompt, a helper function and a foundation model**.',
            'Trust it only as far as it **agrees with human labels**.',
        ],
    },
    chapters: [
        {
            name: 'What a judge is',
            blurb: 'A model scoring a model against a rubric',
            icon: 'cap',
            steps: [2, 3, 4, 5],
        },
        {
            name: 'Find a judge',
            blurb: 'Models page, Evaluation Models group',
            icon: 'map',
            steps: [6, 7, 8, 9, 10],
        },
        {
            name: 'How it is built',
            blurb: 'Prompt, helper function and foundation model',
            icon: 'grid',
            steps: [11],
        },
        {
            name: 'Does it agree with humans?',
            blurb: 'The report that measures the judge itself',
            icon: 'pulse',
            steps: [12, 13, 14],
        },
    ],
    steps: [
        {
            title: 'A model grading a model',
            whatHappens: 'next, how a judge is set up for answer relevancy.',
            icon: 'cap',
            description:
                'An LLM-as-a-Judge is a language model given a very specific job: read a ' +
                "pipeline's output and score it. Instead of a free-form opinion it follows a " +
                'structured prompt with a rubric, for example "rate this reply from 1 to 5 for ' +
                'how well it answers the customer, where 5 means fully answered and 1 means ' +
                'off topic". Because the model can read like a person and run like software, ' +
                'it can grade thousands of replies for qualities that have no exact answer ' +
                'key. Click Continue.',
            benefits: ['Relevancy', 'Tone', 'Coherence', 'Groundedness'],
        },
        {
            title: 'A judge for answer relevancy',
            whatHappens: 'next, a few honest caveats about judges.',
            icon: 'grid',
            description:
                'The diagram shows how the judge is used on the credit card chatbot. Each test ' +
                'question runs through the pipeline, the reply and the question go to the ' +
                'judge with its rubric, and the judge returns a relevancy score. The important ' +
                'part is the loop underneath: the same replies are also scored by people, and ' +
                'the two sets of scores are compared in a confusion matrix.' +
                '<aside data-ggx-use="Why it matters">That comparison is how you learn ' +
                'whether the judge can be trusted before it grades anything on its own. A ' +
                'judge nobody has checked against people is just a second opinion.</aside>' +
                'Click Continue.',
            image: {
                src: 'https://genguardx.ai/tours/risk-08-judge-relevancy.png',
                title: 'Implementation of LLM-as-a-judge for evaluating answer relevancy',
                caption:
                    'Judge scores are checked against human scores before the judge is trusted.',
            },
        },
        {
            title: 'A few things to keep in mind',
            whatHappens: 'next, techniques that make a judge more reliable.',
            icon: 'list',
            description:
                '<ul data-ggx-list>' +
                '<li>Judges are <strong>versatile</strong>: change the rubric and the same ' +
                'approach measures relevancy, toxicity, coherence or groundedness.</li>' +
                '<li>They are <strong>not perfect</strong>. A judge is itself a model, so it ' +
                'can be inconsistent, biased toward certain phrasings, or simply wrong.</li>' +
                '<li>Treat them as a <strong>supplement to human testing</strong>, not a ' +
                'replacement. Use the judge for scale, keep people in the loop for ' +
                'calibration and for the cases that matter most.</li>' +
                '</ul>' +
                '<aside data-ggx-use="In practice">A good split: the judge scores every reply, ' +
                'people score a sample every month, and the two are compared. When they drift ' +
                'apart, fix the rubric before you trust the next batch.</aside>' +
                'Click Continue.',
        },
        {
            title: 'Making a judge more reliable',
            whatHappens: 'next, a short video on finding a judge in GGX.',
            icon: 'compass',
            description:
                'A few techniques teams use to get more dependable scores. Open each tab, then ' +
                'click Continue.',
            tabs: [
                {
                    label: 'Ask for confidence',
                    html:
                        '<p data-ggx-lead>G-Eval style prompting has the judge reason step by ' +
                        'step before it scores, and weights the score by how sure it is.</p>' +
                        '<aside data-ggx-use="In practice">A "4, but low confidence" is worth ' +
                        'more than a bare "4": it tells you which replies a person should ' +
                        'double-check.</aside>',
                },
                {
                    label: 'Break it down',
                    html:
                        '<p data-ggx-lead>Instead of one big "is this good?", ask a series of ' +
                        'small yes/no questions.</p>' +
                        '<ul data-ggx-list>' +
                        '<li><strong>QAG</strong>: generate targeted questions from the content ' +
                        'and check each answer.</li>' +
                        '<li><strong>DAG</strong>: walk a decision tree of checks, where each ' +
                        'answer decides the next question.</li>' +
                        '</ul>' +
                        '<aside data-ggx-use="In practice">"Did it mention the right card?", ' +
                        '"Did it state the balance?", "Did it invent a fee?" are easier for a ' +
                        'model to get right than "rate this 1 to 5".</aside>',
                },
                {
                    label: 'Go hybrid',
                    html:
                        '<p data-ggx-lead>Let the model do the reading and extraction, then ' +
                        'verify what it extracted with plain code.</p>' +
                        '<aside data-ggx-use="In practice">The judge pulls the balance out of ' +
                        'the reply; code compares it with the retrieved record. The judgement ' +
                        'is deterministic, the reading is not.</aside>',
                },
                {
                    label: 'Several judges',
                    html:
                        '<p data-ggx-lead>Different prompts or different models, combined, ' +
                        'smooth out the quirks of any single one.</p>' +
                        '<aside data-ggx-use="In practice">Two judges that disagree are a ' +
                        'signal, not a problem: route those replies to a person.</aside>',
                },
            ],
        },
        {
            title: 'Where judges live in GGX',
            whatHappens: 'next, we go and find one.',
            icon: 'map',
            description:
                'In GGX a judge is just another model on the Models page, with its own ' +
                'prompt, resources, versions, approvals and reports. This short video walks ' +
                'through finding the judge configurations there. Watch it, then click ' +
                'Continue and we will follow the same path by hand.',
            video: {
                src: 'https://genguardx.ai/tours/llm-judge-model-catalog.mp4',
                title: 'Finding the LLM-as-a-Judge configurations on the Models page',
            },
        },
        {
            target: '[test-id="home-page-card-Data & AI Assets"] [test-id="launch-button"]',
            title: 'Judges are kept with your other models',
            whatHappens: 'the module opens on the Agents page, with Models in its sidebar.',
            icon: 'map',
            description:
                'Every model the project uses, whether it powers a pipeline or grades one, ' +
                'lives in <strong>Data & AI Assets</strong>. Click <strong>Launch</strong> on ' +
                'this card.',
            side: 'bottom',
            align: 'center',
            buttons: ['previous'],
            advanceOnTargetClick: true,
            actionLabel: 'Launch',
        },
        {
            target: '[test-id="sidenav-item-tab-Models"]',
            reveal: 'button[aria-label="Show all assets"]',
            scrollIntoView: true,
            title: 'Open the Models page',
            whatHappens: 'the Models page opens, with models grouped by what they are for.',
            icon: 'map',
            description:
                'The <strong>Models</strong> page lists every foundation model, guardrail ' +
                'and evaluation model registered in the project. Click <strong>Model ' +
                'Catalog</strong>.',
            side: 'right',
            align: 'start',
            buttons: ['previous'],
            advanceOnTargetClick: true,
            actionLabel: 'Models',
        },
        {
            target: '[test-id^="group-Evaluation Models"]',
            targetText: 'Evaluation Models (LLM as a Judge)',
            climbToRow: true,
            waitForPresent: '[test-id="model-type-filter"]',
            advanceWhenPresent:
                '[test-id^="grid-row-link-Answer Relevancy (Credit Card Pipeline)"]',
            title: 'Judges have their own group',
            whatHappens: 'the group expands to list every judge in the project.',
            icon: 'grid',
            description:
                'The catalog is grouped by purpose: the foundation LLMs that power pipelines, ' +
                'the guardrails and metrics, and <strong>Evaluation Models (LLM as a ' +
                'Judge)</strong>, which is where every judge is kept. Click it to open the ' +
                'group.',
            side: 'bottom',
            align: 'start',
            buttons: ['previous'],
            advanceOnTargetClick: true,
            actionLabel: 'Evaluation Models (LLM as a Judge)',
        },
        {
            target: '[test-id^="grid-row-link-Answer Relevancy (Credit Card Pipeline)"]',
            targetText: 'Answer Relevancy (Credit Card Pipeline) LLM as a Judge',
            climbToRow: true,
            scrollIntoView: true,
            title: 'One judge per question',
            whatHappens: "the judge's page opens on its Details tab.",
            icon: 'search',
            description:
                'Each judge here answers one question about a reply: is it relevant, is it ' +
                'toxic, is it a jailbreak attempt, is it grounded. <strong>Answer Relevancy ' +
                '(Credit Card Pipeline) LLM as a Judge</strong> scores how well the credit card ' +
                "chatbot's replies address what the customer asked. Click it to open it.",
            side: 'bottom',
            align: 'start',
            buttons: ['previous'],
            advanceOnTargetClick: true,
            actionLabel: 'Answer Relevancy (Credit Card Pipeline) LLM as a Judge',
        },
        {
            target: '[test-id="inputs"]',
            frame: true,
            scrollIntoView: true,
            title: 'What the judge is made of',
            whatHappens: 'next, the report that checks the judge against humans.',
            icon: 'grid',
            description:
                'A judge is assembled from shared, versioned objects, not written from ' +
                'scratch. <strong>Resources & Inputs</strong> lists its three parts:' +
                '<ul data-ggx-list>' +
                '<li><strong>The rubric</strong>: <code>cc_response_accuracy_prompt</code> ' +
                'tells the model to act as a US banking call-center expert and rate each reply ' +
                'from 1 to 5 for how well it answers the customer.</li>' +
                '<li><strong>The helper</strong>: <code>extract_response_to_customer</code> ' +
                "pulls the reply out of the pipeline's output.</li>" +
                '<li><strong>The reader</strong>: <code>gemini_2_0_flash</code> is the ' +
                'foundation model that does the judging.</li>' +
                '</ul>' +
                '<aside data-ggx-use="In practice">Swap any one of them and you have a new ' +
                'version of the judge, reviewed and approved like anything else.</aside>' +
                'Click Continue.',
            side: 'top',
            align: 'start',
            buttons: ['previous', 'next'],
        },
        {
            target: '[test-id="tab-item-Dashboard"]',
            scrollIntoView: true,
            title: 'The judge gets judged too',
            whatHappens: 'the Dashboard opens on the latest run, with one tab per report.',
            icon: 'pulse',
            description:
                'Because a judge is a model, it has a dashboard of its own. The reports here ' +
                'do not score the chatbot; they score the judge, by comparing its ratings with ' +
                'the ratings people gave the same replies. Click <strong>Dashboard</strong>.',
            side: 'bottom',
            align: 'start',
            buttons: ['previous'],
            advanceOnTargetClick: true,
            actionLabel: 'Dashboard',
        },
        {
            target: '[test-id^="jobtab-Accuracy of LLM-as-a-judge (Credit Card"]',
            scrollIntoView: true,
            title: 'Open the judge accuracy report',
            whatHappens: 'the report opens inside the dashboard.',
            icon: 'grid',
            description:
                'There is one accuracy report for credit card questions and one for everything ' +
                'else. Click <strong>Accuracy of LLM-as-a-judge (Credit Card Queries)</strong>.',
            side: 'bottom',
            align: 'start',
            buttons: ['previous'],
            advanceOnTargetClick: true,
            actionLabel: 'Accuracy of LLM-as-a-judge (Credit Card Queries)',
        },
        {
            target: '[test-id="figure-Executive Summary"] h1',
            frame: true,
            scrollIntoView: true,
            title: 'How much does it agree with people?',
            whatHappens: 'the tour wraps up.',
            icon: 'pulse',
            description:
                'This report treats the human labels as the truth and the judge as the thing ' +
                'under test:' +
                '<ul data-ggx-list>' +
                '<li><strong>Accuracy, precision, recall, F1</strong>: how often the ' +
                "judge's call matches a person's.</li>" +
                "<li><strong>Kendall's tau</strong>: whether it ranks replies in the same " +
                'order people do.</li>' +
                '<li><strong>Confusion matrix</strong>: exactly where the two disagree, score ' +
                'by score.</li>' +
                '</ul>' +
                '<aside data-ggx-use="How to read it">Treat it like a new hire\'s first ' +
                'reviews: strong agreement means the judge can grade at scale, weak agreement ' +
                'means the rubric needs work before you rely on it.</aside>' +
                'Click Continue to finish.',
            side: 'bottom',
            align: 'start',
            buttons: ['previous', 'next'],
        },
    ],
};
